import React, { useState } from 'react';
import { useRole } from './RoleContext';

/**
 * One-time "complete your profile" gate. Cognito self-signup only collects an
 * email, so a person's name is captured here on first login (unless it was
 * backfilled from a known source). Blocks the app until first + last name are
 * set; after that `needsProfile` is false and this never renders again.
 */
export default function ProfileGate({ children }: { children: React.ReactNode }) {
  const { user, saveProfile } = useRole();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user?.needsProfile) return <>{children}</>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!first.trim() || !last.trim()) {
      setError('Please enter both your first and last name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveProfile(first.trim(), last.trim());
    } catch {
      setError('Something went wrong saving your name. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <form style={card} onSubmit={submit}>
        <h1 style={{ fontSize: '1.25rem', margin: '0 0 .25rem' }}>Welcome to the Fintech Innovation Lab</h1>
        <p style={{ color: '#64748b', margin: '0 0 1.25rem', fontSize: '.9rem' }}>
          Tell us your name so we can personalize your experience.
        </p>
        <label style={label}>First name
          <input style={input} value={first} onChange={e => setFirst(e.target.value)} autoFocus />
        </label>
        <label style={label}>Last name
          <input style={input} value={last} onChange={e => setLast(e.target.value)} />
        </label>
        {error && <p style={{ color: '#dc2626', fontSize: '.85rem', margin: '0 0 .75rem' }}>{error}</p>}
        <button type="submit" style={button} disabled={saving}>
          {saving ? 'Saving…' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  fontFamily: 'system-ui, sans-serif',
};
const card: React.CSSProperties = {
  background: '#fff', padding: '2rem 2.25rem', borderRadius: 12, width: 360,
  boxShadow: '0 10px 40px rgba(0,0,0,.2)',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: '.8rem', fontWeight: 600, color: '#334155', marginBottom: '.75rem',
};
const input: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: '.35rem', padding: '.6rem .75rem',
  border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '.95rem', boxSizing: 'border-box',
};
const button: React.CSSProperties = {
  width: '100%', marginTop: '.5rem', padding: '.7rem', background: '#3b82f6', color: '#fff',
  border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '.95rem', cursor: 'pointer',
};
